<template>
  <ModalConfirmation
    :showClose="false"
    :isSubmitButtonLoading="isSubmitting"
    :customWidth="'650px'"
    :noMarginOnButton="true"
    @close="$emit('close')"
    @confirm="onConfirmAwayStatus"
    :isShowConfirmDeleteMessage="false">
    <div slot="custom_warning">
      <!-- Markér som ude af huset (away = true) -->
      <div class="away_warning" v-if="!isAway">
        <div v-if="canceledRows.length > 0" class="away_warning__lessons">
          <strong>{{ i18n.lessons_will_be_cancelled }}</strong>
          <table class="away_warning__lessons__table">
            <thead>
              <tr>
                <th class="away_warning__th-check">
                  <input type="checkbox" :checked="allCancelSelected" @change="toggleAllCancel" />
                </th>
                <th>{{ i18n.class_header }}</th>
                <th>{{ i18n.lesson_header }}</th>
                <th>{{ i18n.time_header }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in canceledRows" :key="row.event_id">
                <td class="away_warning__td-check">
                  <input type="checkbox" v-model="selectedCancelIds" :value="row.event_id" />
                </td>
                <td>{{ row.klassNames }}</td>
                <td>{{ row.title }}</td>
                <td class="text-gray">{{ humanTime(row.start, row.end) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="away_warning__text">
          <h4 class="marg-0-bottom">{{ i18n.confirm_away_title }}</h4>
          <p class="pad-1 italic pad-0-top">{{ i18n.confirm_away_text }}</p>
          <p class="pad-1 italic pad-0-top" v-if="canceledRows.length === 0">
            {{ i18n.no_lessons_to_cancel }}
          </p>
        </div>
      </div>

      <!-- Fortryd 'ude af huset' (away = false) -->
      <div class="away_warning" v-else>
        <div v-if="uncancelGroups.length > 0" class="away_warning__lessons">
          <strong>{{ i18n.lessons_will_be_restored }}</strong>
          <div v-for="grp in uncancelGroups" :key="'u_'+grp.name">
            <table class="away_warning__lessons__table">
              <tbody>
              <tr v-for="ev in grp.events" :key="ev.event_id + '_' + grp.name" class="marg-0-5-top">
                <td>
                  {{ grp.name }} | {{ ev.title }}
                  <span class="text-gray">({{ humanTime(ev.start, ev.end) }})</span>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="away_warning__text">
          <h4 class="marg-0-bottom">{{ i18n.confirm_unaway_title }}</h4>
          <p class="pad-1 italic">{{ i18n.confirm_unaway_text }}</p>
          <p class="pad-1 italic pad-0-top" v-if="uncancelGroups.length === 0">
            {{ i18n.no_lessons_to_restore }}
          </p>
        </div>
      </div>
    </div>

    <p slot="confirmText">{{ !isAway ? i18n.mark_as_away : i18n.unmark_as_away }}</p>
  </ModalConfirmation>
</template>


<script>
import ModalConfirmation from '../Modals/ModalConfirmation.vue'
import docendo from '../../docendo'
import moment from 'moment'

function eventKlassIds (ev) {
  const calendars = (ev && (ev.calendars || ev.calendar_events)) || []
  return calendars
    .filter(function (c) { return c && c.calendar_type === 'klass' })
    .map(function (c) { return c.calendar_id })
}

function eventHasOtherKlasses (ev, tripKlassIds) {
  const klassIds = eventKlassIds(ev)
  if (klassIds.length === 0) return false
  const tripSet = tripKlassIds || []
  return klassIds.some(function (id) {
    return tripSet.indexOf(id) === -1
  })
}

const I18N = {
  da: {
    mark_as_away: 'Markér som ude af huset',
    unmark_as_away: 'Fortryd markering som ude af huset',
    confirm_away_title: 'Er du sikker på, at du vil markere lektionen som ude af huset?',
    confirm_away_text: 'Lektionen vil blive markeret som værende uden for skolens område. Og personale på lektionen vil fremgå i vikardækningen som fraværende.',
    confirm_unaway_text: 'Lektionen vil ikke længere fremgå som ude af huset. Og personale vil ikke længere fremgå i vikardækningen som fraværende.',
    confirm_unaway_title: 'Er du sikker på, at du vil fortryde markeringen som ude af huset?',
    lessons_will_be_cancelled: 'Følgende lektioner vil blive aflyst',
    lessons_will_be_restored: 'Følgende lektioner vil blive gendannet',
    no_lessons_to_cancel: 'Der er ingen lektioner at aflyse i perioden',
    no_lessons_to_restore: 'Der er ingen lektioner at gendanne i perioden',
    class_header: 'Klasse',
    lesson_header: 'Lektion',
    time_header: 'Tid'
  },
  nb: {
    mark_as_away: 'Marker som utenfor skolen',
    unmark_as_away: 'Angre markering som utenfor skolen',
    confirm_away_title: 'Er du sikker på at du vil markere timen som utenfor skolen?',
    confirm_away_text: 'Timen vil bli markert som utenfor skolens område. Personalet på timen vil vises i vikarhåndteringen som fraværende.',
    confirm_unaway_title: 'Er du sikker på at du vil angre markeringen som utenfor skolen?',
    confirm_unaway_text: 'Timen vil ikke lenger vises som utenfor skolen. Personalet vil ikke lenger vises i vikarhåndteringen som fraværende.',
    lessons_will_be_cancelled: 'Følgende timer vil bli avlyst',
    lessons_will_be_restored: 'Følgende timer vil bli gjenopprettet',
    no_lessons_to_cancel: 'Det er ingen timer å avlyse i perioden',
    no_lessons_to_restore: 'Det er ingen timer å gjenopprette i perioden',
    class_header: 'Klasse',
    lesson_header: 'Time',
    time_header: 'Tid'
  },
  en: {
    mark_as_away: 'Mark as out of school',
    unmark_as_away: 'Undo out of school marking',
    confirm_away_title: 'Are you sure you want to mark the event as out of school?',
    confirm_away_text: 'The event will be marked as taking place outside the school premises. And staff in the event will appear in the substitute management as absent.',
    confirm_unaway_text: 'The event will no longer be shown as taking place outside the school. And staff will no longer appear in the substitute management as absent.',
    confirm_unaway_title: 'Are you sure you want to undo the out of school marking?',
    lessons_will_be_cancelled: 'The following lessons will be canceled',
    lessons_will_be_restored: 'The following lessons will be restored',
    no_lessons_to_cancel: 'There are no lessons to cancel in the period',
    no_lessons_to_restore: 'There are no lessons to restore in the period',
    class_header: 'Class',
    lesson_header: 'Lesson',
    time_header: 'Time'
  }
}

export default {
  components: {ModalConfirmation},
  props: {
    event: {
      type: Object,
      default: () => ({})
    },
    eventIdProp: {
      default: 'event_id'
    }
  },
  data () {
    return {
      isSubmitting: false,
      klassEvents: [],
      isLoadingKlassLesson: false,
      selectedCancelIds: []
    }
  },
  watch: {
    canceledCandidates: {
      immediate: true,
      handler (candidates) {
        var tripKlassIds = this.tripKlassIds
        // Vælg kun events, der ikke også hører til andre klasser (fx fælles valgfag)
        this.selectedCancelIds = candidates
          .filter(function (ev) {
            return !eventHasOtherKlasses(ev, tripKlassIds)
          })
          .map(function (ev) { return ev.event_id })
      }
    }
  },
  computed: {
    isAway () {
      return this.event && this.event.away === true
    },
    selfEventId () {
      if (!this.event) return null
      if (this.event[this.eventIdProp] != null) return this.event[this.eventIdProp]
      return this.event.event_id != null ? this.event.event_id : this.event.id
    },
    eventCalendars () {
      if (!this.event) return []
      if (Array.isArray(this.event.calendars) && this.event.calendars.length > 0) {
        return this.event.calendars
      }
      if (Array.isArray(this.event.calendar_events)) {
        return this.event.calendar_events
      }
      return []
    },
    tripKlassIds () {
      return this.eventCalendars
        .filter(function (calendar) {
          return calendar && calendar.calendar_type === 'klass'
        })
        .map(function (calendar) {
          return calendar.calendar_id
        })
    },
    locale () {
      return this.$store.getters.locale
    },
    i18n () {
      const locale = this.locale === 'nn' || this.locale === 'no' ? 'nb' : this.locale
      return I18N[locale] || I18N.en
    },
    canceledCandidates () {
      if (!Array.isArray(this.klassEvents)) return []
      var selfId = this.selfEventId
      return this.klassEvents.filter(ev => {
        const notAlreadyCanceled = ev && ev.canceled !== true
        const notAlreadyAway = ev && ev.away !== true
        const notSelf = !selfId || (ev.event_id !== selfId && ev.id !== selfId)
        return notAlreadyCanceled && notAlreadyAway && notSelf
      })
    },
    // Bruges ved un-away: hvad kan gendannes?
    uncancelCandidates () {
      if (!Array.isArray(this.klassEvents)) return []
      var selfId = this.selfEventId
      return this.klassEvents.filter(ev => {
        const isCanceled = ev && ev.canceled === true
        // rør ikke events der er away=true i forvejen
        const notAway = ev && ev.away !== true
        const notSelf = !selfId || (ev.event_id !== selfId && ev.id !== selfId)
        return isCanceled && notAway && notSelf
      })
    },
    canceledRows () {
      var seen = {}
      var rows = []
      this.canceledCandidates.forEach(function (ev) {
        if (seen[ev.event_id]) return
        seen[ev.event_id] = true
        var klassNames = (ev.calendars || [])
          .filter(function (c) { return c && c.calendar_type === 'klass' })
          .map(function (c) { return c.name })
          .sort(function (a, b) { return a.localeCompare(b) })
        rows.push({
          event_id: ev.event_id,
          title: ev.title,
          start: ev.start,
          end: ev.end,
          klassNames: klassNames.length > 0 ? klassNames.join(', ') : 'Uden klasse'
        })
      })
      rows.sort(function (a, b) {
        return moment(a.start).valueOf() - moment(b.start).valueOf()
      })
      return rows
    },
    // Grupper kandidater til gendannelse pr. klasse
    uncancelGroups () {
      return this.groupByClass(this.uncancelCandidates)
    },
    allCancelSelected () {
      return this.canceledCandidates.length > 0 &&
        this.selectedCancelIds.length === this.canceledCandidates.length
    }
  },
  methods: {
    toggleAllCancel () {
      if (this.allCancelSelected) {
        this.selectedCancelIds = []
      } else {
        this.selectedCancelIds = this.canceledCandidates.map(function (ev) { return ev.event_id })
      }
    },
    groupByClass (events) {
      if (!Array.isArray(events)) return []
      const bucket = {}
      events.forEach(function (ev) {
        const calendars = (ev.calendars || []).filter(function (c) {
          return c && c.calendar_type === 'klass'
        })
        if (calendars.length === 0) {
          const key = 'Uden klasse'
          if (!bucket[key]) bucket[key] = []
          bucket[key].push(ev)
        } else {
          calendars.forEach(function (c) {
            const key = c && c.name ? c.name : 'Uden klasse'
            if (!bucket[key]) bucket[key] = []
            bucket[key].push(ev)
          })
        }
      })
      // Sortér grupper alfabetisk og events kronologisk
      return Object.keys(bucket)
        .sort(function (a, b) {
          return a.localeCompare(b)
        })
        .map(function (name) {
          const list = bucket[name].slice().sort(function (a, b) {
            return moment(a.start).valueOf() - moment(b.start).valueOf()
          })
          return {name: name, events: list}
        })
    },
    humanTime (startIso, endIso) {
      const s = moment(startIso)
      const e = moment(endIso)
      return s.format('LT') + ' - ' + e.format('LT')
    },
    onConfirmAwayStatus () {
      this.handleConfirmAwayStatus()
    },
    handleConfirmAwayStatus () {
      this.isSubmitting = true

      const willSetAwayTo = !this.isAway
      const updatedAway = {
        school_id: this.event.school_id,
        updates: [{event_id: this.event[this.eventIdProp], away: willSetAwayTo}]
      }

      docendo.updateEvents(
        updatedAway.school_id,
        updatedAway.updates,
        success => {
          if (willSetAwayTo) {
            // Netop sat away=true → batch-cancel kun valgte kandidater
            var self = this
            var selected = this.canceledCandidates.filter(function (ev) {
              return self.selectedCancelIds.indexOf(ev.event_id) !== -1
            })
            if (selected.length > 0) {
              const updates = selected.map(function (ev) {
                return {event_id: ev.event_id, canceled: true}
              })
              docendo.updateEvents(
                this.event.school_id,
                updates,
                s2 => {
                  this.isSubmitting = false
                  this.$emit('updated')
                },
                f2 => {
                  this.isSubmitting = false
                  console.log('failed to bulk cancel')
                  this.$emit('updated')
                }
              )
            } else {
              this.isSubmitting = false
              this.$emit('updated')
              this.$store.dispatch('trackEvent', 'calendar_event_away')
            }
          } else {
            // Netop sat away=false → batch-uncancel kandidater
            if (this.uncancelCandidates.length > 0) {
              const updates = this.uncancelCandidates.map(function (ev) {
                return {event_id: ev.event_id, canceled: false}
              })
              docendo.updateEvents(
                this.event.school_id,
                updates,
                s3 => {
                  this.isSubmitting = false
                  this.$emit('updated')
                },
                f3 => {
                  this.isSubmitting = false
                  console.log('failed to bulk uncancel')
                  this.$emit('updated')
                }
              )
            } else {
              this.isSubmitting = false
              this.$emit('updated')
              this.$store.dispatch('trackEvent', 'calendar_event_awayFalse')
            }
          }
        },
        failure => {
          this.isSubmitting = false
          console.log('failed to toggle away')
        }
      )
    },
    readKlassEvents () {
      this.isLoadingKlassLesson = true
      const dbEventStart = moment(this.event.start).format('YYYY-MM-DD HH:mm')
      const dbEventEnd = moment(this.event.end).format('YYYY-MM-DD HH:mm')

      const calendarIds = this.eventCalendars
        .filter(function (calendar) {
          return calendar.calendar_type === 'klass'
        })
        .map(function (calendar) {
          return calendar.calendar_id
        })

      if (calendarIds.length === 0) {
        this.klassEvents = []
        this.isLoadingKlassLesson = false
        return
      }

      docendo.calendarEvents2(
        calendarIds,
        dbEventStart,
        dbEventEnd,
        null,
        success => {
          this.klassEvents = success && success.length > 0 ? JSON.parse(success) : []
          this.isLoadingKlassLesson = false
        },
        failure => {
          this.showToast('Error fetching class events', false)
          this.isLoadingKlassLesson = false
        }
      )
    }
  },
  mounted () {
    this.readKlassEvents()
  }
}
</script>

<style scoped>
.away_warning {
  display: flex;
  flex-direction: row-reverse;
  align-items: stretch;
  max-height: calc(100vh - 50px);
  overflow-y: auto;
}

.away_warning > div {
  flex: 1;
}

.away_warning__lessons {
  padding: 2rem 1rem;
  background: #edf0f5;
  min-width: 400px;
}

.away_warning__lessons__table {
  width: 100%;
  border-width: 1px 1px 0 1px;
  border-collapse: collapse;
  background: #f6f9fc;
  margin-top: 0.5rem;
  border-radius: 0.25rem;
}

.away_warning__lessons__table th,
.away_warning__lessons__table td {
  padding: 0.3rem 0.5rem;
  text-align: left;
}

.away_warning__lessons__table thead tr {
  border-bottom: 1px solid #d0d6df;
}

.away_warning__lessons__table thead th {
  vertical-align: middle;
}

.away_warning__th-check,
.away_warning__td-check {
  width: 1.5rem;
  padding: 0;
}

.away_warning__th-check input,
.away_warning__td-check input {
  display: block;
  margin: 0 auto;
}

</style>



// WEBPACK FOOTER //
// DModalOutOfSchoolLesson.vue?1e20677e